import { Fragment, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Text,
} from "recharts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import { Stack, Typography } from "@/common/styles/style";
import { formattedDate, generateGraphColors } from "@/helpers/graphHelpers";
import { TrendItemType, TrendType } from "@/pages/dashboard/type";
import { styles } from "./style";
import { GraphProps, UpdatedDataProps } from "./type";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

export default function Graph({
  selectedNames,
  saved,
  startDate,
  endDate,
  isWidget,
}: GraphProps) {
  const [oneDay, setOneDay] = useState(false);
  const startDateObj = dayjs(startDate, "YYYY-MM-DD HH:mm").tz(
    dayjs.tz.guess(),
    true
  );
  const endDateObj = dayjs(endDate, "YYYY-MM-DD HH:mm").tz(
    dayjs.tz.guess(),
    true
  );

  const xAxisTicks = useMemo(() => {
    const start = startDate ? dayjs(startDate) : dayjs().subtract(7, "days");
    const end = endDate ? dayjs(endDate) : dayjs();

    const ticks: string[] = [];

    if (
      startDateObj.isSame(endDateObj, "day") ||
      startDateObj.add(1, "day").isSame(endDateObj, "day")
    ) {
      setOneDay(true);
      const sortedData = saved
        ? saved.flatMap((item: TrendItemType) =>
            Object.values(item)
              .flat()
              .sort((a, b) =>
                dayjs(a.timestamp).isAfter(dayjs(b.timestamp)) ? 1 : -1
              )
          )
        : [];
      const uniqueTime = Array.from(
        new Set(
          sortedData?.map((item) =>
            dayjs(item.timestamp).format("YYYY-MM-DD HH:mm")
          )
        )
      );
      ticks.push(
        dayjs(startDate).format("YYYY-MM-DD HH:mm"),
        ...uniqueTime,
        dayjs(endDate).format("YYYY-MM-DD HH:mm")
      );
    } else {
      setOneDay(false);
      for (
        let date = start.clone();
        date.isBefore(end) || date.isSame(end, "day");
        date = date.add(1, "day")
      ) {
        ticks.push(date.format("YYYY-MM-DD"));
      }
    }
    return ticks;
  }, [startDate, endDate, saved]);

  const formatXAxis = (tickItem: string) => {
    const currentDate = tickItem;
    return formattedDate(oneDay, currentDate);
  };

  const chartData = useMemo(() => {
    if (!saved || saved.length === 0) return [];

    const flattenedData = saved.flatMap((collectedDataItem) => {
      const name = Object.keys(collectedDataItem)[0];

      return Object.values(collectedDataItem)?.flatMap((items: TrendType[]) => {
        return items.map((item) => ({
          timestamp: dayjs(item.timestamp).format("YYYY-MM-DD HH:mm"),
          date: dayjs(item.timestamp).format("YYYY-MM-DD"),
          time: dayjs(item.timestamp).format("HH:mm"),
          [name]: item.value,
        }));
      });
    });

    const findMatchingItem = (targetDate: string) =>
      flattenedData.find(
        (item) =>
          item.timestamp === dayjs(targetDate).format("YYYY-MM-DD HH:mm")
      );

    const findPreviousItem = (targetDate: string) => {
      const sortedData = flattenedData.sort((a, b) =>
        dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1
      );
      const previousItem = sortedData.filter((item) =>
        dayjs(item.timestamp).isBefore(dayjs(targetDate))
      );
      return previousItem.at(-1);
    };

    const matchingStartItem = findMatchingItem(startDate as string);
    const matchingEndItem = findMatchingItem(endDate as string);

    let updatedData: UpdatedDataProps[] = [];

    if (!matchingStartItem && !matchingEndItem) {
      const formattedStartDate = dayjs(startDateObj).format("YYYY-MM-DD HH:mm");
      const formattedEndDate = dayjs(endDateObj).format("YYYY-MM-DD HH:mm");
      const name = Object.keys(saved[0])[0];

      updatedData = [
        {
          timestamp: formattedStartDate,
          date: formattedStartDate.split(" ")[0],
          time: formattedStartDate.split(" ")[1],
          [name]: null,
        },
        ...flattenedData,
        {
          timestamp: formattedEndDate,
          date: formattedEndDate.split(" ")[0],
          time: formattedEndDate.split(" ")[1],
          [name]: null,
        },
      ];
    } else {
      updatedData = [...flattenedData];

      if (!matchingStartItem) {
        const firstTimestamp = dayjs(
          flattenedData[0]?.timestamp || startDateObj
        )
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        const previousItem = findPreviousItem(firstTimestamp);
        updatedData.unshift({
          timestamp: previousItem ? previousItem.timestamp : firstTimestamp,
          date: firstTimestamp.split(" ")[0],
          time: firstTimestamp.split(" ")[1],
          v1: null,
        });
      }

      if (!matchingEndItem) {
        const lastTimestamp = dayjs(
          flattenedData.at(-1)?.timestamp || endDateObj
        )
          .endOf("day")
          .format("YYYY-MM-DD HH:mm");
        const previousItem = findPreviousItem(lastTimestamp);
        updatedData.push({
          timestamp: previousItem ? previousItem.timestamp : lastTimestamp,
          date: lastTimestamp.split(" ")[0],
          time: lastTimestamp.split(" ")[1],
          v1: null,
        });
      }
    }

    return updatedData.sort((a, b) =>
      dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1
    );
  }, [saved, startDate, endDate]);

  const graphColor = useMemo(() => generateGraphColors(saved), [saved]);

  const renderCustomYAxisTick = ({
    x,
    y,
    payload,
    type,
  }: {
    x: number;
    y: number;
    payload: { value: string };
    type: string;
  }) => {
    const matchedColor = graphColor.find((color) => color.yAxisId === type);
    const fillColor = matchedColor ? matchedColor.stroke : "#8884d8";

    return (
      <g transform={`translate(${x},${y})`}>
        <Text x={0} y={0} fill={fillColor} textAnchor="end" fontSize={13}>
          {payload.value}
        </Text>
      </g>
    );
  };

  if (!saved || saved.length === 0) {
    return (
      <Stack sx={styles.nodata}>
        <Typography>データがありません</Typography>
      </Stack>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      {selectedNames && selectedNames.length > 0 ? (
        (() => {
          const fakeData = selectedNames.map((name) => ({
            name,
            value: name,
          }));

          return (
            <LineChart data={fakeData}>
              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={styles.legend}
              />
              {fakeData.map((item) => (
                <Line
                  key={item.name}
                  type="monotone"
                  dataKey={item.name}
                  stroke="blue"
                />
              ))}
            </LineChart>
          );
        })()
      ) : (
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 10,
            bottom: 5,
          }}
        >
          <Legend
            verticalAlign="top"
            align="center"
            wrapperStyle={styles.legend}
          />
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={oneDay ? "timestamp" : "date"}
            ticks={xAxisTicks}
            tickFormatter={formatXAxis}
          />
          <Tooltip
            formatter={(value: number, _: string, props: any) => {
              const formattedTimestamp = dayjs(props.payload.timestamp).format(
                "YYYY-MM-DD HH:mm"
              );

              return (
                <>
                  <span>{value !== undefined ? value : "No Value"}</span>
                  <div style={styles.tooltip}>{formattedTimestamp}</div>
                </>
              );
            }}
            labelFormatter={() => ""}
          />
          {saved.map((collectedDataItem, index) => {
            const name = Object.keys(collectedDataItem)[0];
            const matchedColor = graphColor.find(
              (color) => color.yAxisId === name
            );
            const strokeColor = matchedColor ? matchedColor.stroke : "#8884d8";

            return (
              <Fragment key={`graph-${index}`}>
                {!isWidget && (
                  <YAxis
                    tick={(props) =>
                      renderCustomYAxisTick({
                        ...props,
                        type: name,
                      })
                    }
                    domain={["auto", "auto"]}
                    axisLine={false}
                    tickLine={false}
                    orientation="left"
                    yAxisId={name}
                    width={50}
                  />
                )}
                <Line
                  key={`line-${index}`}
                  type="monotone"
                  dataKey={name}
                  stroke={strokeColor}
                  dot={false}
                  yAxisId={name}
                  connectNulls={true}
                  isAnimationActive={false}
                />
              </Fragment>
            );
          })}
        </LineChart>
      )}
    </ResponsiveContainer>
  );
}
